/**
 * Contains functions that construct GraphQL queries to be used with Shopify's
 * Storefront API client.
 *
 * Query strings can be optionally added in ./storefront-queries.ts so they can
 * be imported without pulling in the request layer (e.g. from checkout
 * extensions).
 */

import {makeShopifyRequest} from "../request";
import {castToMetaobject, getValue} from "../operation-utils";
import {isRegistrationOpen} from "../wasm-shared/registration-open";
import type {
	CartCreateEmptyMutation,
	CartCreateMutation,
	CartCreateMutationVariables,
	CartLinesAddMutation,
	GetCartQuery,
	GetProductVariantQuery,
	GetProductVariantQueryVariables,
	GetVariantClassScheduleQuery,
} from "../codegen/storefront.generated";
import type {CartInput} from "../codegen/storefront.types";

export type ProductVariant = {
	id: string;
	title: string;
	availableForSale: boolean;
	priceAmount: string;
	priceCurrencyCode: string;
	productTitle: string;
};

export type CartItemCustomAttribute = {
	/**
	 * Human-readable label shown next to the value in the Shopify admin's
	 * order/cart view.
	 */
	label: string;
	value: string;
	/**
	 * When true, attribute is hidden from the customer-facing cart and checkout
	 */
	isHidden?: boolean;
};

/**
 * A cart-level (not per-line-item) labelled value. Shopify carries these over
 * to the resulting order's top-level `note_attributes` after checkout — use
 * this for data that applies once per order (e.g. the purchaser's id), not
 * per line item.
 */
export type CartAttribute = {
	/**
	 * Human-readable label shown next to the value in the Shopify admin's
	 * order view.
	 */
	label: string;
	value: string;
};

export type CartItem = {
	productVariantId: string;
	quantity: number;
	/** Labelled values attached to the item. */
	customAttributes?: CartItemCustomAttribute[];
};

export type CartCreationInput = {
	items: CartItem[];
	attributes?: CartAttribute[];
	/**
	 * Buyer email stamped on the cart's `buyerIdentity` at creation.
	 * Used later to verify the cart belongs to the logged-in shopper.
	 */
	email?: string;
};

export type Cart = {
	id: string;
	checkoutUrl: string;
};

/**
 * Fetch a product variant by its Shopify Global ID. Returns `null` if the
 * node ID doesn't resolve to a ProductVariant. Throws if the Shopify request
 * fails.
 */
export async function getProductVariant(
	id: string
): Promise<ProductVariant | null> {
	const variables: GetProductVariantQueryVariables = {id};
	// makeShopifyRequest gets the codegen'd type
	const data = await makeShopifyRequest<GetProductVariantQuery>(
		"storefront",
		`#graphql
			query GetProductVariant($id: ID!) {
				node(id: $id) {
					__typename
					... on ProductVariant {
						id
						title
						availableForSale
						price {
							amount
							currencyCode
						}
						product {
							title
						}
					}
				}
			}
		`,
		variables
	);
	// `node` returns the abstract Node interface; a valid ID for a non-variant
	// resource (e.g. a Product) still resolves to a non-null node with no
	// ProductVariant fields. Use __typename to check if we actually fetched the
	// correct resource
	if (!data.node || data.node.__typename !== "ProductVariant") return null;
	return {
		id: data.node.id,
		title: data.node.title,
		availableForSale: data.node.availableForSale,
		priceAmount: data.node.price.amount,
		priceCurrencyCode: data.node.price.currencyCode,
		productTitle: data.node.product.title,
	};
}

export type CartLineAttribute = {
	key: string;
	value: string;
};

export type CartLine = {
	id: string;
	quantity: number;
	variantId: string;
	variantTitle: string;
	productTitle: string;
	priceAmount: string;
	priceCurrencyCode: string;
	course_id: string;
	/** Registrar course offering UUID from the course_offering metafield. */
	course_offering_id: string;
	/** Academy campus_id from the course_offering campus reference, when present. */
	campus_id: string;
	/**
	 * Whether registration is open for this variant, from
	 * `custom.is_registration_open` (missing/empty treated as open).
	 */
	isRegistrationOpen: boolean;
	attributes: CartLineAttribute[];
};

export type CartBuyerIdentityCustomer = {
	id: string;
	email: string | null;
	firstName: string | null;
	lastName: string | null;
	displayName: string;
};

/** Buyer info attached to a Shopify cart (email, phone, linked customer, etc.). */
export type CartBuyerIdentity = {
	email: string | null;
	phone: string | null;
	countryCode: string | null;
	customer: CartBuyerIdentityCustomer | null;
};

export type CartDetails = {
	id: string;
	checkoutUrl: string;
	createdAt: string;
	updatedAt: string;
	lines: CartLine[];
	/** Present when returned from `getCart`; other cart ops may omit it. */
	buyerIdentity?: CartBuyerIdentity;
};

function mapBuyerIdentity(buyerIdentity: {
	email?: string | null;
	phone?: string | null;
	countryCode?: string | null;
	customer?: {
		id: string;
		email?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		displayName: string;
	} | null;
}): CartBuyerIdentity {
	return {
		email: buyerIdentity.email ?? null,
		phone: buyerIdentity.phone ?? null,
		countryCode: buyerIdentity.countryCode ?? null,
		customer: buyerIdentity.customer
			? {
					id: buyerIdentity.customer.id,
					email: buyerIdentity.customer.email ?? null,
					firstName: buyerIdentity.customer.firstName ?? null,
					lastName: buyerIdentity.customer.lastName ?? null,
					displayName: buyerIdentity.customer.displayName,
				}
			: null,
	};
}

export async function createEmptyCart(): Promise<CartDetails> {
	const data = await makeShopifyRequest<CartCreateEmptyMutation>(
		"storefront",
		`#graphql
			mutation CartCreateEmpty {
				cartCreate(input: {}) {
					cart {
						id
						checkoutUrl
						createdAt
						updatedAt
						lines(first: 1) {
							edges {
								node {
									id
									quantity
									merchandise {
										__typename
									}
								}
							}
						}
					}
					userErrors {
						message
					}
				}
			}
		`,
		{}
	);

	if (!data.cartCreate) {
		throw new Error("Shopify returned no cartCreate payload");
	}
	const {cart, userErrors} = data.cartCreate;
	if (userErrors.length) {
		throw new Error(
			`Cart creation failed: ${userErrors.map((e) => e.message).join("; ")}`
		);
	}
	if (!cart) {
		throw new Error("Shopify returned no cart");
	}

	return {
		id: cart.id,
		checkoutUrl: cart.checkoutUrl,
		createdAt: cart.createdAt,
		updatedAt: cart.updatedAt,
		/* TODO Should this just return cart.lines even though there is supposed
		to be nothing in case there is something so we can throw. */
		lines: [],
	};
}

/**
 * @returns `null` when cart deleted (e.g. post-checkout) or expired (30 days post-creation)
 */
export async function getCart(cartId: string): Promise<CartDetails | null> {
	const data = await makeShopifyRequest<GetCartQuery>(
		"storefront",
		`#graphql
			query GetCart($cartId: ID!) {
				cart(id: $cartId) {
					id
					checkoutUrl
					createdAt
					updatedAt
					buyerIdentity {
						email
						phone
						countryCode
						customer {
							id
							email
							firstName
							lastName
							displayName
						}
					}
					lines(first: 100) {
						edges {
							node {
								id
								quantity
								attributes {
									key
									value
								}
								merchandise {
									__typename
									... on ProductVariant {
										id
										title
										price {
											amount
											currencyCode
										}
										isRegistrationOpenMetafield: metafield(
											namespace: "custom"
											key: "is_registration_open"
										) {
											value
										}
										product {
											title
											courseOfferingMetafield: metafield(namespace: "custom", key: "course_offering") {
												reference {
													__typename
													... on Metaobject {
														courseIdField: field(key: "course_id") { value }
														courseOfferingIdField: field(key: "course_offering_id") { value }
														campusField: field(key: "campus") {
															reference {
																__typename
																... on Metaobject {
																	campusIdField: field(key: "campus_id") { value }
																}
															}
														}
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{cartId}
	);

	if (!data.cart) return null;

	const lines: CartLine[] = data.cart.lines.edges.flatMap(({node}) => {
		const merch = node.merchandise;
		if (merch.__typename !== "ProductVariant") return [];

		const courseOffering = castToMetaobject(
			merch.product.courseOfferingMetafield
		);
		const campus = castToMetaobject(courseOffering?.campusField);

		return [
			{
				id: node.id,
				quantity: node.quantity,
				variantId: merch.id,
				variantTitle: merch.title,
				productTitle: merch.product.title,
				priceAmount: merch.price.amount,
				priceCurrencyCode: merch.price.currencyCode,
				course_id: getValue(courseOffering?.courseIdField) ?? "",
				course_offering_id:
					getValue(courseOffering?.courseOfferingIdField) ?? "",
				campus_id: getValue(campus?.campusIdField) ?? "",
				isRegistrationOpen: isRegistrationOpen(
					merch.isRegistrationOpenMetafield?.value
				),
				attributes: node.attributes.flatMap(({key, value}) =>
					value != null ? [{key, value}] : []
				),
			},
		];
	});

	return {
		id: data.cart.id,
		checkoutUrl: data.cart.checkoutUrl,
		createdAt: data.cart.createdAt,
		updatedAt: data.cart.updatedAt,
		buyerIdentity: mapBuyerIdentity(data.cart.buyerIdentity),
		lines,
	};
}

/**
 * Returns the updated cart from the mutation response so callers don't need a
 * follow-up `getCart` (which could fail after the lines were already added).
 *
 * @throws on Shopify `userErrors`
 */
export async function addCartLines(
	cartId: string,
	items: CartItem[]
): Promise<CartDetails> {
	const data = await makeShopifyRequest<CartLinesAddMutation>(
		"storefront",
		`#graphql
			mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
				cartLinesAdd(cartId: $cartId, lines: $lines) {
					cart {
						id
						checkoutUrl
						createdAt
						updatedAt
						lines(first: 100) {
							edges {
								node {
									id
									quantity
									attributes {
										key
										value
									}
									merchandise {
										__typename
										... on ProductVariant {
											id
											title
											price {
												amount
												currencyCode
											}
											isRegistrationOpenMetafield: metafield(
												namespace: "custom"
												key: "is_registration_open"
											) {
												value
											}
											product {
												title
											}
										}
									}
								}
							}
						}
					}
					userErrors {
						message
					}
				}
			}
		`,
		{
			cartId,
			lines: items.map((item) => ({
				merchandiseId: item.productVariantId,
				quantity: item.quantity,
				attributes: item.customAttributes
					? item.customAttributes.map(({label, value, isHidden}) => ({
							key: isHidden ? `_${label}` : label,
							value,
						}))
					: undefined,
			})),
		}
	);

	const errors = data.cartLinesAdd?.userErrors ?? [];
	if (errors.length) {
		throw new Error(
			`Failed to add cart lines: ${errors.map((e) => e.message).join("; ")}`
		);
	}

	const cart = data.cartLinesAdd?.cart;
	if (!cart) {
		throw new Error("Shopify returned no cart");
	}

	const lines: CartLine[] = cart.lines.edges.flatMap(({node}) => {
		const merch = node.merchandise;
		if (merch.__typename !== "ProductVariant") return [];

		return [
			{
				id: node.id,
				quantity: node.quantity,
				variantId: merch.id,
				variantTitle: merch.title,
				productTitle: merch.product.title,
				priceAmount: merch.price.amount,
				priceCurrencyCode: merch.price.currencyCode,
				course_id: "",
				course_offering_id: "",
				campus_id: "",
				isRegistrationOpen: isRegistrationOpen(
					merch.isRegistrationOpenMetafield?.value
				),
				attributes: node.attributes.flatMap(({key, value}) =>
					value != null ? [{key, value}] : []
				),
			},
		];
	});

	return {
		id: cart.id,
		checkoutUrl: cart.checkoutUrl,
		createdAt: cart.createdAt,
		updatedAt: cart.updatedAt,
		lines,
	};
}

export {removeCartLines} from "./storefront/removeCartLines";

/**
 * Create a Shopify cart with the given items. Returns the checkout URL.
 * Throws if the Shopify request fails, if Shopify returns mutation-level
 * `userErrors`, or if the response is missing the expected cart payload.
 */
export async function createCart(input: CartCreationInput): Promise<Cart> {
	const shopifyInput: CartInput = {
		lines: input.items.map((item) => ({
			merchandiseId: item.productVariantId,
			quantity: item.quantity,
			// custom attributes show up next to the product in the cart. Shopify
			// hides any attribute whose key starts with "_" from the
			// customer-facing cart/checkout
			attributes: item.customAttributes?.map(({label, value, isHidden}) => ({
				key: isHidden ? `_${label}` : label,
				value,
			})),
		})),
		// cart-level attributes carry over to the order's top-level
		// note_attributes after checkout — one value per order, unlike
		// line-item attributes above.
		attributes: input.attributes?.map(({label, value}) => ({
			key: label,
			value,
		})),
		// Email alone is enough to claim the cart for a shopper; linking a
		// Customer object requires a customerAccessToken we don't have here.
		...(input.email ? {buyerIdentity: {email: input.email}} : {}),
	};
	const variables: CartCreateMutationVariables = {input: shopifyInput};

	// makeShopifyRequest gets the codegen'd type
	const data = await makeShopifyRequest<CartCreateMutation>(
		"storefront",
		`#graphql
			mutation CartCreate($input: CartInput!) {
				cartCreate(input: $input) {
					cart {
						id
						checkoutUrl
					}
					userErrors {
						message
					}
				}
			}
		`,
		variables
	);

	if (!data.cartCreate) {
		throw new Error("Shopify returned no cartCreate payload");
	}

	const {cart, userErrors} = data.cartCreate;
	if (userErrors.length) {
		throw new Error(
			`Cart creation failed: ${userErrors.map((e: (typeof userErrors)[0]) => e.message).join("; ")}`
		);
	}
	if (!cart) {
		throw new Error("Shopify returned no cart");
	}

	return {id: cart.id, checkoutUrl: cart.checkoutUrl};
}

export type MeetingTime = {
	day: string | null;
	timeStartAt: string | null;
	timeEndAt: string | null;
};

export type VariantClassSchedule = {
	classId: string | null;
	dateStartAt: string | null;
	dateEndAt: string | null;
	timezone: string | null;
	meetingTimes: MeetingTime[];
};

export const CLASS_SCHEDULE_AND_BUILDING_FRAGMENT = `#graphql
	fragment ClassScheduleAndBuilding on Metaobject {
		classIdField: field(key: "class_id") { value }
		scheduleField: field(key: "schedule") {
			reference {
				__typename
				... on Metaobject {
					dateStartAt: field(key: "date_start_at") { value }
					dateEndAt: field(key: "date_end_at") { value }
					timezoneField: field(key: "timezone") { value }
					meetingTimes: field(key: "meetings_times") {
						references(first: 20) {
							nodes {
								__typename
								... on Metaobject {
									day: field(key: "day") { value }
									timeStartAt: field(key: "time_start_at") { value }
									timeEndAt: field(key: "time_end_at") { value }
								}
							}
						}
					}
				}
			}
		}
		campusBuildingField: field(key: "campus_building") {
			reference {
				__typename
				... on Metaobject {
					buildingIdField: field(key: "building_id") { value }
				}
			}
		}
	}`;

/**
 * TODO: This is for the custom-receipt POC. This will need some cleanup if we
 * want to reuse the function.
 * Fetches the class schedule metaobject from a variant's custom.class metafield.
 * Returns null if the variant has no class metafield or no schedule.
 *
 * Metaobject chain:
 *   variant → metafields.custom.class (metaobject_reference)
 *     → class.schedule (metaobject_reference)
 *       → class_schedule.{ date_start_at, date_end_at, timezone }
 */
export async function getVariantClassSchedule(
	variantId: string
): Promise<VariantClassSchedule | null> {
	const data = await makeShopifyRequest<GetVariantClassScheduleQuery>(
		"storefront",
		`#graphql
			query GetVariantClassSchedule($id: ID!) {
				node(id: $id) {
					__typename
					... on ProductVariant {
						metafield(namespace: "custom", key: "class") {
							reference {
								__typename
								... on Metaobject {
									...ClassScheduleAndBuilding
								}
							}
						}
					}
				}
			}
		${CLASS_SCHEDULE_AND_BUILDING_FRAGMENT}
		`,
		{id: variantId}
	);

	if (!data.node || data.node.__typename !== "ProductVariant") return null;
	const classRef = data.node.metafield?.reference;
	if (!classRef || classRef.__typename !== "Metaobject") return null;

	const scheduleRef = classRef.scheduleField?.reference;
	const meetingNodes =
		scheduleRef?.__typename === "Metaobject"
			? (scheduleRef.meetingTimes?.references?.nodes ?? [])
			: [];

	return {
		classId: classRef.classIdField?.value ?? null,
		dateStartAt:
			scheduleRef?.__typename === "Metaobject"
				? (scheduleRef.dateStartAt?.value ?? null)
				: null,
		dateEndAt:
			scheduleRef?.__typename === "Metaobject"
				? (scheduleRef.dateEndAt?.value ?? null)
				: null,
		timezone:
			scheduleRef?.__typename === "Metaobject"
				? (scheduleRef.timezoneField?.value ?? null)
				: null,
		meetingTimes: meetingNodes
			.filter((n) => n.__typename === "Metaobject")
			.map((n) => ({
				day: n.day?.value ?? null,
				timeStartAt: n.timeStartAt?.value ?? null,
				timeEndAt: n.timeEndAt?.value ?? null,
			})),
	};
}
